import React, { useState, useEffect, useCallback } from 'react';
import { 
  Typography, 
  Box, 
  Tabs, 
  Tab, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Grid,
  InputLabel,
  FormControl,
  Container,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  LinearProgress,
  alpha
} from '@mui/material';
import { useNotification } from '../contexts/NotificationContext';
import { getUsers, updateUser, createUser } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import BadgeIcon from '@mui/icons-material/Badge';
import BusinessIcon from '@mui/icons-material/Business';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';

// Paleta profesional
const COLORS = {
  gradient1: '#00b894',
  gradient2: '#0984e3',
  primary: '#0984e3',
  success: '#00b894',
  warning: '#eb9800ff',
  error: '#d63031',
  info: '#74b9ff',
  purple: '#6c5ce7',
  orange: '#e17055',
  dark: '#2d3436'
};

// Colores por rol
const ROLE_COLORS = {
  'admin': { color: COLORS.error, label: '👑 Administrador', icon: '👑' },
  'supervisor': { color: COLORS.warning, label: '👔 Supervisor', icon: '👔' },
  'user': { color: COLORS.success, label: '👤 Usuario', icon: '👤' }
};

// Colores por departamento
const DEPT_COLORS = {
  'Producción': COLORS.success,
  'Mantenimiento': COLORS.warning,
  'Calidad': COLORS.primary,
  'Proceso': COLORS.purple,
  'Administración': COLORS.orange,
  'Logística': COLORS.info,
  'General': '#636e72'
};

const departmentOptions = [
  'Producción',
  'Mantenimiento',
  'Calidad',
  'Proceso',
  'Administración',
  'Logística',
  'General'
];

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminDashboard = () => {
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editedUser, setEditedUser] = useState({});
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    role: '',
    department: '',
    nombre: '',
    apellido: ''
  });
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    } else {
      fetchUsers();
    }
  }, [user, navigate, fetchUsers]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const filteredUsers = users.filter(user => 
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.apellido?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDetailClick = (user) => {
    setSelectedUser(user);
    setIsDetailDialogOpen(true);
  };

  const handleEditClick = (user) => {
    setEditedUser(user);
    setIsEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setIsEditDialogOpen(false);
    setEditedUser({});
  };

  const handleEditSave = async () => {
    try {
      await updateUser(editedUser.id, editedUser);
      showNotification('Usuario actualizado con éxito', 'success');
      fetchUsers();
      handleEditClose();
    } catch (error) {
      console.error('Error updating user:', error);
      showNotification('Error al actualizar el usuario', 'error');
    }
  };

  const handleNewUserChange = (event) => {
    setNewUser({
      ...newUser,
      [event.target.name]: event.target.value
    });
  };

  const handleCreateUser = async () => {
    try {
      await createUser(newUser);
      showNotification('Usuario creado con éxito', 'success');
      fetchUsers();
      setNewUser({
        username: '',
        email: '',
        password: '',
        role: '',
        department: '',
        nombre: '',
        apellido: ''
      });
      setTabValue(0); // Volver a la lista
    } catch (error) {
      console.error('Error creating user:', error);
      showNotification('Error al crear el usuario', 'error');
    }
  };

  const getRoleTheme = (role) => ROLE_COLORS[role] || ROLE_COLORS['user'];
  const getDeptColor = (dept) => DEPT_COLORS[dept] || '#636e72';

  const tabs = [
    { label: 'Usuarios', icon: <PeopleIcon /> },
    { label: 'Crear Usuario', icon: <PersonAddIcon /> }
  ];

  // Loading state
  if (loading) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh',
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center',
          background: 'linear-gradient(180deg, #f8fffe 0%, #f0f7ff 100%)'
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            boxShadow: `0 8px 32px ${alpha(COLORS.gradient1, 0.3)}`
          }}
        >
          <AdminPanelSettingsIcon sx={{ fontSize: 40, color: 'white' }} />
        </Box>
        <Typography variant="h6" sx={{ color: COLORS.dark, fontWeight: 600, mb: 2 }}>
          Cargando usuarios...
        </Typography>
        <Box sx={{ width: 200 }}>
          <LinearProgress 
            sx={{ 
              borderRadius: 2,
              height: 6,
              backgroundColor: alpha(COLORS.gradient1, 0.2),
              '& .MuiLinearProgress-bar': {
                background: `linear-gradient(90deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                borderRadius: 2
              }
            }} 
          />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography color="error" variant="h5">{error}</Typography>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5">Por favor, inicia sesión para acceder.</Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #f8fffe 0%, #f0f7ff 100%)' }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
            borderRadius: 4,
            p: { xs: 3, md: 4 },
            mb: 4,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: `0 20px 60px ${alpha(COLORS.gradient1, 0.3)}`
          }}
        >
          <Box sx={{ position: 'absolute', top: -40, right: -40, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
          <Box sx={{ position: 'absolute', bottom: -20, left: '40%', width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <AdminPanelSettingsIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 900, letterSpacing: -0.5 }}>
                Panel de Administración
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                {users.length} usuarios registrados
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Main Content */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '2px solid #e0e0e0',
            overflow: 'hidden'
          }}
        >
          {/* Tabs */}
          <Box sx={{ borderBottom: '2px solid #e0e0e0', background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  background: `linear-gradient(90deg, ${COLORS.gradient1}, ${COLORS.gradient2})`
                }
              }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          backgroundColor: tabValue === index ? alpha(COLORS.primary, 0.15) : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {React.cloneElement(tab.icon, {
                          sx: { fontSize: 20, color: tabValue === index ? COLORS.primary : '#9e9e9e' }
                        })}
                      </Box>
                      <Typography sx={{ fontWeight: tabValue === index ? 700 : 500, fontSize: '0.9rem', color: tabValue === index ? COLORS.primary : '#666' }}>
                        {tab.label}
                      </Typography>
                    </Box>
                  }
                  sx={{ textTransform: 'none', minHeight: 64, px: 3 }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Tab 0: Lista de Usuarios */}
          <TabPanel value={tabValue} index={0}>
            {/* Search Bar */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                borderRadius: 2,
                border: '2px solid #e0e0e0',
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                flexWrap: 'wrap'
              }}
            >
              <TextField
                placeholder="Buscar por nombre o usuario..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: COLORS.gradient1 }} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  flexGrow: 1,
                  minWidth: 250,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': { borderColor: COLORS.gradient1 },
                    '&.Mui-focused fieldset': { borderColor: COLORS.gradient1 }
                  }
                }}
              />
              <Chip
                label={`${filteredUsers.length} usuarios`}
                sx={{
                  bgcolor: alpha(COLORS.primary, 0.1),
                  color: COLORS.primary,
                  fontWeight: 600
                }}
              />
            </Paper>

            {/* Users Table */}
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '2px solid #e0e0e0'
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    {['Usuario', 'Nombre', 'Apellidos', 'Rol', 'Departamento', 'Acciones'].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.9rem',
                          py: 2
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map((u, index) => {
                    const roleTheme = getRoleTheme(u.role);
                    return (
                      <TableRow
                        key={u.id}
                        sx={{
                          backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fffe',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: alpha(COLORS.gradient1, 0.08),
                            transform: 'scale(1.001)'
                          }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                              sx={{
                                width: 36,
                                height: 36,
                                bgcolor: alpha(COLORS.primary, 0.15),
                                color: COLORS.primary,
                                fontWeight: 700,
                                fontSize: '0.9rem'
                              }}
                            >
                              {u.username?.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography sx={{ fontWeight: 600 }}>@{u.username}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 500 }}>{u.nombre || '-'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 500 }}>{u.apellido || '-'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={roleTheme.label}
                            size="small"
                            sx={{
                              bgcolor: alpha(roleTheme.color, 0.15),
                              color: roleTheme.color,
                              fontWeight: 600,
                              border: 'none'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={<BusinessIcon />}
                            label={u.department || 'Sin asignar'}
                            size="small"
                            sx={{
                              bgcolor: alpha(getDeptColor(u.department), 0.15),
                              color: getDeptColor(u.department),
                              fontWeight: 600,
                              border: 'none',
                              '& .MuiChip-icon': { color: getDeptColor(u.department) }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Ver detalles">
                            <IconButton
                              onClick={() => handleDetailClick(u)}
                              sx={{ color: COLORS.info, '&:hover': { bgcolor: alpha(COLORS.info, 0.1) } }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar">
                            <IconButton
                              onClick={() => handleEditClick(u)}
                              sx={{ color: COLORS.primary, '&:hover': { bgcolor: alpha(COLORS.primary, 0.1) } }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Tab 1: Crear Usuario */}
          <TabPanel value={tabValue} index={1}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                border: '2px solid #e0e0e0',
                maxWidth: 800,
                mx: 'auto'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: alpha(COLORS.success, 0.15),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <PersonAddIcon sx={{ color: COLORS.success, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>
                    Nuevo Usuario
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#636e72' }}>
                    Complete los campos para crear un nuevo usuario
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nombre de usuario"
                    name="username"
                    value={newUser.username}
                    onChange={handleNewUserChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BadgeIcon sx={{ color: COLORS.primary }} />
                        </InputAdornment>
                      )
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Correo electrónico"
                    name="email"
                    value={newUser.email}
                    onChange={handleNewUserChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: COLORS.primary }} />
                        </InputAdornment>
                      )
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contraseña"
                    name="password"
                    type="password"
                    value={newUser.password}
                    onChange={handleNewUserChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: COLORS.primary }} />
                        </InputAdornment>
                      )
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Rol</InputLabel>
                    <Select
                      label="Rol"
                      name="role"
                      value={newUser.role}
                      onChange={handleNewUserChange}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="user">
                        <Chip label="👤 Usuario" size="small" sx={{ bgcolor: alpha(COLORS.success, 0.15), color: COLORS.success, fontWeight: 600, border: 'none' }} />
                      </MenuItem>
                      <MenuItem value="supervisor">
                        <Chip label="👔 Supervisor" size="small" sx={{ bgcolor: alpha(COLORS.warning, 0.15), color: COLORS.warning, fontWeight: 600, border: 'none' }} />
                      </MenuItem>
                      <MenuItem value="admin">
                        <Chip label="👑 Administrador" size="small" sx={{ bgcolor: alpha(COLORS.error, 0.15), color: COLORS.error, fontWeight: 600, border: 'none' }} />
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Departamento</InputLabel>
                    <Select
                      label="Departamento"
                      name="department"
                      value={newUser.department}
                      onChange={handleNewUserChange}
                      sx={{ borderRadius: 2 }}
                    >
                      {departmentOptions.map((dept) => (
                        <MenuItem key={dept} value={dept}>
                          <Chip
                            icon={<BusinessIcon />}
                            label={dept}
                            size="small"
                            sx={{
                              bgcolor: alpha(getDeptColor(dept), 0.15),
                              color: getDeptColor(dept),
                              fontWeight: 600,
                              border: 'none',
                              '& .MuiChip-icon': { color: getDeptColor(dept) }
                            }}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nombre"
                    name="nombre"
                    value={newUser.nombre}
                    onChange={handleNewUserChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: COLORS.primary }} />
                        </InputAdornment>
                      )
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Apellidos"
                    name="apellido"
                    value={newUser.apellido}
                    onChange={handleNewUserChange}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<PersonAddIcon />}
                    onClick={handleCreateUser}
                    sx={{
                      background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                      fontWeight: 700,
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      boxShadow: `0 4px 14px ${alpha(COLORS.gradient1, 0.4)}`,
                      '&:hover': {
                        boxShadow: `0 6px 20px ${alpha(COLORS.gradient1, 0.5)}`
                      }
                    }}
                  >
                    Crear Usuario
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </TabPanel>
        </Paper>

        {/* Dialog: Detalles del Usuario */}
        <Dialog 
          open={isDetailDialogOpen} 
          onClose={() => setIsDetailDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle
            sx={{
              background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <InfoIcon />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Detalles del Usuario</Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {selectedUser && (
              <Box sx={{ pt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      bgcolor: alpha(COLORS.primary, 0.15),
                      color: COLORS.primary,
                      fontWeight: 800,
                      fontSize: '1.5rem'
                    }}
                  >
                    {selectedUser.username?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {selectedUser.nombre} {selectedUser.apellido}
                    </Typography>
                    <Typography sx={{ color: '#636e72' }}>@{selectedUser.username}</Typography>
                  </Box>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#636e72' }}>Email</Typography>
                    <Typography sx={{ fontWeight: 600 }}>{selectedUser.email}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#636e72' }}>Rol</Typography>
                    <Box>
                      <Chip
                        label={getRoleTheme(selectedUser.role).label}
                        size="small"
                        sx={{
                          bgcolor: alpha(getRoleTheme(selectedUser.role).color, 0.15),
                          color: getRoleTheme(selectedUser.role).color,
                          fontWeight: 600,
                          border: 'none'
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#636e72' }}>Departamento</Typography>
                    <Box>
                      <Chip
                        label={selectedUser.department || 'Sin asignar'}
                        size="small"
                        sx={{
                          bgcolor: alpha(getDeptColor(selectedUser.department), 0.15),
                          color: getDeptColor(selectedUser.department),
                          fontWeight: 600,
                          border: 'none'
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#636e72' }}>Fecha de registro</Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {new Date(selectedUser.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button 
              onClick={() => setIsDetailDialogOpen(false)}
              sx={{ color: '#666', fontWeight: 600 }}
            >
              Cerrar
            </Button>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => {
                setIsDetailDialogOpen(false);
                handleEditClick(selectedUser);
              }}
              sx={{
                background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                fontWeight: 700,
                borderRadius: 2
              }}
            >
              Editar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog: Editar Usuario */}
        <Dialog 
          open={isEditDialogOpen} 
          onClose={handleEditClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle
            sx={{
              background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <EditIcon />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Editar Usuario</Typography>
            </Box>
            <IconButton onClick={handleEditClose} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Nombre de usuario"
                value={editedUser.username || ''}
                onChange={(e) => setEditedUser({...editedUser, username: e.target.value})}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><BadgeIcon sx={{ color: COLORS.primary }} /></InputAdornment>
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                label="Correo electrónico"
                value={editedUser.email || ''}
                onChange={(e) => setEditedUser({...editedUser, email: e.target.value})}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: COLORS.primary }} /></InputAdornment>
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                label="Nueva contraseña (dejar vacío para no cambiar)"
                type="password"
                onChange={(e) => setEditedUser({...editedUser, password: e.target.value})}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: COLORS.primary }} /></InputAdornment>
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <FormControl fullWidth>
                <InputLabel>Rol</InputLabel>
                <Select
                  label="Rol"
                  value={editedUser.role || ''}
                  onChange={(e) => setEditedUser({...editedUser, role: e.target.value})}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="user">👤 Usuario</MenuItem>
                  <MenuItem value="supervisor">👔 Supervisor</MenuItem>
                  <MenuItem value="admin">👑 Administrador</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Departamento</InputLabel>
                <Select
                  label="Departamento"
                  value={editedUser.department || ''}
                  onChange={(e) => setEditedUser({...editedUser, department: e.target.value})}
                  sx={{ borderRadius: 2 }}
                >
                  {departmentOptions.map((dept) => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Nombre"
                value={editedUser.nombre || ''}
                onChange={(e) => setEditedUser({...editedUser, nombre: e.target.value})}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: COLORS.primary }} /></InputAdornment>
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                label="Apellidos"
                value={editedUser.apellido || ''}
                onChange={(e) => setEditedUser({...editedUser, apellido: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, gap: 1 }}>
            <Button onClick={handleEditClose} sx={{ color: '#666', fontWeight: 600 }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleEditSave}
              sx={{
                background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                fontWeight: 700,
                px: 3,
                borderRadius: 2
              }}
            >
              Guardar cambios
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default AdminDashboard;